import { useState, useEffect, useCallback, useRef } from 'react';
import { GroupedOverviewData, MonitorOverviewRequest } from '@/types/datachart';
import { fetchOverviewData } from '@/services/datachart';

interface UseOverviewDataReturn {
  data: GroupedOverviewData | null;
  loading: boolean;
  error: string | null;
  refetch: (params?: Partial<MonitorOverviewRequest>) => Promise<void>;
}

// Default request parameters
const defaultParams: MonitorOverviewRequest = {
  period: 'all',
  page_no: 1,
  page_size: 10,
};

export const useOverviewData = (initialParams?: Partial<MonitorOverviewRequest>): UseOverviewDataReturn => {
  const [data, setData] = useState<GroupedOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<MonitorOverviewRequest>({
    ...defaultParams,
    ...initialParams,
  });
  
  const hasInitialized = useRef(false);
  const lastFetchTime = useRef<number>(0);
  const CACHE_DURATION = 30000; // 30 seconds cache

  const fetchData = useCallback(async (requestParams: MonitorOverviewRequest, force = false) => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime.current;
    
    // Skip if recently fetched and not forced
    if (!force && timeSinceLastFetch < CACHE_DURATION && data) {
      console.log('Skipping API call - using cached data');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchOverviewData(requestParams);
      setData(result);
      lastFetchTime.current = now;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch overview data';
      setError(errorMessage);
      console.error('Error fetching overview data:', err);
    } finally {
      // Add timeout to allow chart animations to complete
      setTimeout(() => {
        setLoading(false);
      }, 1500); // 1.5 seconds to allow animations to finish
    }
  }, [data]);

  const refetch = useCallback(async (newParams?: Partial<MonitorOverviewRequest>) => {
    const updatedParams = { ...params, ...newParams };
    setParams(updatedParams);
    await fetchData(updatedParams, true); // Force fetch on manual refetch
  }, [params, fetchData]);

  // Initial fetch only once when component mounts
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchData(params);
    }
  }, [params, fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};

// Hook for individual data sections
export const useOverviewDataSections = (initialParams?: Partial<MonitorOverviewRequest>) => {
  const { data, loading, error, refetch } = useOverviewData(initialParams);

  return {
    overall: data?.overall || null,
    period: data?.period || null,
    comparison: data?.comparison || null,
    growth: data?.growth || null,
    lastUpdated: data?.last_updated || null,
    inviteCodes: data?.invite_codes || [],
    recentDirectUsers: data?.recent_direct_users || [],
    commissionChartData: data?.commission_chart_data || [],
    customersChartData: data?.customers_chart_data || [],
    loading,
    error,
    refetch,
  };
};
